import React, { useEffect, useState } from 'react';
import { cn } from '../poker-ui/utils/cn';
import { LOBBY_HOME_ASSETS } from '../lib/lobbyHomeAssets';
import { MODE_TILES } from './lobbyHubShared';

const ART_BTN =
  'lobby-focus relative z-[1] m-0 flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-visible border-0 bg-transparent p-0 shadow-none outline-none transition-all duration-200 ease-out motion-reduce:transition-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40';

const ART_IMG =
  'pointer-events-none mx-auto h-auto w-full max-h-[5.5rem] origin-center object-contain transition-transform duration-200 ease-out motion-reduce:transition-none select-none sm:max-h-[6rem]';

const TAB_ACTIVE_SEKA = 'scale-[1.04]';
const TAB_ACTIVE_BELOT = 'scale-[1.04]';
const TAB_IDLE = 'opacity-[0.62] hover:opacity-[0.82] active:opacity-90';

const PLAY_IMG_ACTIVE = 'scale-[1.06] sm:scale-[1.08]';
const PLAY_IMG_IDLE = 'scale-[0.96]';

function isLobbyPlayBotsTile(tile) {
  return tile.id === 'local' || String(tile.id).includes('bots');
}

function playTileDisabled(tile, socket) {
  return Boolean(tile.disabled) || (isLobbyPlayBotsTile(tile) && !socket);
}

/** Крупный заголовок секции как «ИГРАТЬ» в макете — над рядом Сека/Белот. */
export function LobbyHomeKataHeading({ className }) {
  return (
    <h2
      className={cn(
        'mb-2 px-1 text-left text-[1.625rem] font-black uppercase leading-[1.05] tracking-[0.1em] text-white sm:mb-2.5 sm:text-[1.85rem] sm:tracking-[0.11em]',
        className
      )}
    >
      Катать
    </h2>
  );
}

/**
 * Ряд «Сека столы | Белот столы» — заменяет блок `LobbyGameSwitcher` (без покера), стили выбора как у старых вкладок.
 * @param {'seka'|'belot'} [initialMode] — какой режим подсвечен при входе (`belot` на хабе Белота).
 * @param {() => void} [onSelectSeka] — при выборе Секи (например `onBack` с хаба Белота в лобби Секи).
 */
export function LobbyHomeTablesModeRow({
  initialMode = 'seka',
  onSelectSeka,
  onOpenBelot,
  className,
}) {
  const [activeMode, setActiveMode] = useState(initialMode);
  const sekaOn = activeMode === 'seka';
  const belotOn = activeMode === 'belot';

  useEffect(() => {
    setActiveMode(initialMode);
  }, [initialMode]);

  return (
    <div
      className={cn(
        'mb-3 flex w-full min-w-0 items-stretch gap-2 overflow-visible py-1 px-1.5 sm:mb-4 sm:gap-2.5 sm:px-2 sm:py-1.5',
        className
      )}
      role="tablist"
      aria-label="Столы по игре"
    >
      <button
        type="button"
        role="tab"
        aria-selected={sekaOn}
        aria-label="Столы Сека"
        title="Сека"
        onClick={() => {
          setActiveMode('seka');
          onSelectSeka?.();
        }}
        className={cn(ART_BTN, 'basis-0', sekaOn ? TAB_ACTIVE_SEKA : TAB_IDLE)}
      >
        <img
          src={LOBBY_HOME_ASSETS.btnSekaTables}
          alt=""
          className={cn(ART_IMG, sekaOn ? PLAY_IMG_ACTIVE : PLAY_IMG_IDLE)}
          draggable={false}
        />
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={belotOn}
        aria-label="Столы Белот"
        title="Белот"
        onClick={() => {
          setActiveMode('belot');
          onOpenBelot?.();
        }}
        className={cn(ART_BTN, 'basis-0', belotOn ? TAB_ACTIVE_BELOT : TAB_IDLE)}
      >
        <img
          src={LOBBY_HOME_ASSETS.btnBelotTables}
          alt=""
          className={cn(ART_IMG, belotOn ? PLAY_IMG_ACTIVE : PLAY_IMG_IDLE)}
          draggable={false}
        />
      </button>
    </div>
  );
}

/**
 * «Играть с ботами | Онлайн» — те же колбэки, что у `LobbyPlayModesRow` (по умолчанию `MODE_TILES`, на белоте — `BELOT_LOBBY_PLAY_TILES`).
 */
export function LobbyHomePlayTilesRow({ onTileClick, socket, tiles, className }) {
  const list = tiles ?? MODE_TILES;

  const [activePlayId, setActivePlayId] = useState(() => {
    const list2 = tiles ?? MODE_TILES;
    const firstOk = list2.find((t) => !playTileDisabled(t, socket));
    return (firstOk ?? list2[0])?.id;
  });

  useEffect(() => {
    const list2 = tiles ?? MODE_TILES;
    setActivePlayId((prev) => {
      const stillValid =
        prev != null &&
        list2.some((t) => String(t.id) === String(prev) && !playTileDisabled(t, socket));
      if (stillValid) return prev;
      const firstOk = list2.find((t) => !playTileDisabled(t, socket));
      return (firstOk ?? list2[0])?.id;
    });
  }, [tiles, socket]);

  return (
    <div
      className={cn(
        'mb-3 flex w-full min-w-0 items-stretch gap-2 overflow-visible py-1 px-1.5 sm:mb-4 sm:gap-3 sm:px-2 sm:py-1.5',
        className
      )}
      role="group"
      aria-label="Режим игры"
    >
      {list.map((tile) => {
        const disabled = playTileDisabled(tile, socket);
        const src = isLobbyPlayBotsTile(tile)
          ? LOBBY_HOME_ASSETS.btnPlayBots
          : LOBBY_HOME_ASSETS.btnOnlineNetwork;
        const visuallyActive = !disabled && activePlayId === tile.id;
        return (
          <button
            key={tile.id}
            type="button"
            disabled={disabled}
            aria-pressed={visuallyActive}
            aria-label={`${tile.label}: ${tile.sub}`}
            title={`${tile.label} · ${tile.sub}`}
            onClick={() => {
              if (!disabled) setActivePlayId(tile.id);
              onTileClick(tile);
            }}
            className={cn(ART_BTN, 'basis-0', visuallyActive ? TAB_ACTIVE_SEKA : TAB_IDLE)}
          >
            <img
              src={src}
              alt=""
              className={cn(ART_IMG, visuallyActive ? PLAY_IMG_ACTIVE : PLAY_IMG_IDLE)}
              draggable={false}
            />
            <span className="sr-only">
              {tile.label} {tile.sub}
            </span>
          </button>
        );
      })}
    </div>
  );
}
